import {Component, OnDestroy, OnInit} from '@angular/core';
import {Product} from "../product";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../store';
import {ActivityAction, IActivity} from "../../../activities/activity";
import {Website} from "../website";
import {DbModalService} from "../../../modules/modal/modal";
import {filter, switchMap, takeUntil} from "rxjs/operators";
import {Observable} from "rxjs";
import {Subject} from "rxjs";
import {User} from "../../../user/user";
import {combineLatest} from "rxjs";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {UserSubscription, UserSubscriptionCategory} from "../../../user/user-subscription";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {
  NotificationDialogComponent
} from "../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {TextUtils} from "../../../shared/utils/text-utils";
import {UserSubscriptionStatus} from "../../../user/user-subscription-status";
import {clientRoles} from "../../../user/user-roles-actions";
import {Utils} from "../../../shared/utils/utils";
import {RouteDataService} from "../../../shared/services/route-data/route-data.service";
import {DbModalRef} from "../../../modules/modal/modal-ref";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {CapitalizeFirstPipe} from "../../../shared/pipes/db-capitilizeFirst.pipe";
import {OneMonthFreeComponent} from "../../../shared/components/dialogs/one-month-free/one-month-free.component";

@Component({
    selector: 'db-ecommerce',
    templateUrl: './ecommerce.component.html',
    styleUrls: ['./ecommerce.component.scss'],
    standalone: false
})
export class EcommerceComponent implements OnInit, OnDestroy {

  public website: Website;
  public user: User;
  public userSubscription: UserSubscription;
  public woocommercePlugin: Product;
  public userLocalStorage: any;
  public demoUser: boolean = false;
  public loading: boolean;
  public isMember: boolean;
  public pageId;

  public ongoingActivity$: Observable<IActivity>;
  public ongoingBuilderActivity$: Observable<IActivity>;
  public UserSubscriptionCategory = UserSubscriptionCategory;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _routeDataService: RouteDataService,
    private _dialogService: DialogService
  ) {}

  ngOnInit() {
    this.website = this._route.snapshot.parent.data['website'];
    this.woocommercePlugin = this._route.snapshot.data['woocommercePlugin'];
    this.pageId = this._routeDataService.getCurrentRouteData().pageId;
    this.routerEvents();
    this._addEventHandlers();
  }

  public routerEvents(): void {
    this._router.events
        .pipe(filter((event: any) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.pageId = this._router && this._router.url.split("/").pop();
        });
  }

  public ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public installAndActivate(): void {
    if (!this.website.isEcommerceWebsite && (this.userSubscription && this.userSubscription.trialClient && this.demoUser)) {
      this.showCheckoutDialog('Enable Ecommerce Features', 'Dashboard Action', 'Generative AI - Locked action');
    } else if (this.userSubscription && !this.userSubscription.isEcommerce && !this.userSubscription.plan.additionalDomainCount
      && ![UserSubscriptionCategory.ENTERPRISE, UserSubscriptionCategory.ENTERPRISE_ULTIMATE, UserSubscriptionCategory.PROFESSIONAl].includes(this.userSubscription.category)) {
      this._showEcommerceFeaturesEnableDialog();
    } else {
      this.loading = true;
      let network = 0;
      if (this.website.domain_hash) {
        network = 1;
      }
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: this.website,
        products: [this.woocommercePlugin],
        action: ActivityAction.INSTALL_ACTIVATE,
        network: network
      }));
    }
  }

  public showCheckoutDialog(title?, category?, action?): void {
    const _capitalize = new CapitalizeFirstPipe();
    const analyticsData = {
      eventCategory: category || 'Free Upgrade Offer',
      eventAction: action || 'Locked button click',
      eventLabel: title || 'Ecommerce banner - ' + _capitalize.transform(this.pageId === 'orders-list' ? 'Orders' : this.pageId, true),
      websiteId: this.website?.id
    };
    if (this.demoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;
      modal.componentInstance.showAsEcommerce = true;
    }  else {
      const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
      modal.componentInstance.mode = 'feature';
      modal.componentInstance.hideTab = true;
      modal.componentInstance.showAsDemo = true;
    }
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _addEventHandlers(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getCurrentWorkspace)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userLocalStorage, userSubscription, currentWorkspace]) => {
        this.user = user;
        this.userLocalStorage = userLocalStorage;
        this.userSubscription = userSubscription;

        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.isMember = [clientRoles.SITE_DEV, clientRoles.COMPANY_DEV].includes(currentWorkspace?.roleId);
      });
    this._getOngoingWoocommerceState();
    this._getProductOngoingActivity();
  }

  private _getOngoingWoocommerceState(): void {
    this._store.select(fromStore.getWebsiteWoocommercePlugin(this.website.id))
      .subscribe((woocommercePlugin) => {
        if (woocommercePlugin) {
          this.woocommercePlugin = woocommercePlugin;
        }
      });
  }

  private _getProductOngoingActivity(): void {
    this.ongoingActivity$ = this._store.select(fromStore.getProductOngoingActivity(this.website.id, this.woocommercePlugin.slug));
    this.ongoingActivity$
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity => !!activity)
      )
      .subscribe((activity) => {
        this.loading = !(activity.status === 'done' || activity.status === 'failed');
        if (activity.status === 'done') {
          this._router.navigate(['/websites/' + this.website?.id + '/main']);
        }
      });
  }

  private _showEcommerceFeaturesEnableDialog(): void {
    if (this.isMember) {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: 'You don’t have access </br>to Ecommerce',
        message: TextUtils.getText('text_enableEcommerceFeaturesNotAvailableDesc'),
        buttonText: TextUtils.getText('text_availableWebsitesGotIt'),
        buttonColor: 'black',
        action: 'disable',
        hasCloseButton: true
      });
    } else {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header:  TextUtils.getText('text_enableEcommerceFeatures'),
        message: TextUtils.getText('text_enableEcommerceFeaturesDesc'),
        buttonText: TextUtils.getText('text_upgradeToEcommercePlans'),
        buttonColor: 'black',
        action: 'redirect',
        hasCloseButton: true,
        forEcommerce: true
      })
      .subscribe((confirmed: boolean): void => {
        if (confirmed) {
          if (this.userSubscription
            .status === UserSubscriptionStatus.canceled) {
            this._router.navigate(['/workspace/subscription-plan']);
          } else {
            this._router.navigate(['/upgrade-plan'], {queryParams: {active_tab: 'ecommerce'}});
          }
        }
      });
    }
  }

  private addModalProps(modal: DbModalRef, options) {
    for (let option in options) {
      modal.componentInstance[option] = options[option];
    }
  }

}
